import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';

// Styles & Assets
import styles from './PatientPreconfigDialog.module.css';

// Components
import PatientPreconfigParameters from '../PatientPreconfigParameters/PatientPreconfigParameters';
import CancelConfirmButtons from '../../UI/CancelConfirmButtons/CancelConfirmButtons';
import ErrorSpinnerWrapper from '../../UI/ErrorSpinnerWrapper.tsx/ErrorSpinnerWrapper';
import PatientPreconfigHeader from '../PatientPreconfigHeader/PatientPreconfigHeader';
import PatientPreconfigRestore from '../PatientPreconfigRestore/PatientPreconfigRestore';
import PreconfigPollTable from '../../CreatePreconfigurationPage/PreconfigPollTable/PreconfigPollTable';

// Hooks
import {
    useFetchPreconfigData,
    useFetchPatientPreconfig,
    usePatientPreconfigInquiries,
    usePatientPreconfigParameters,
    useSavePatientPreconfig
} from '../../../hooks/preconfig-hooks';

// Interfaces
import { Patient } from '../../../interfaces/Patient';
import {
    PreconfigInquiry,
    PreconfigSheetParameter,
    Preconfiguration
} from '../../../interfaces/Preconfigurations';
import { PatientInquiry } from '../../../interfaces/Poll';

interface Props {
    patient: Patient;
    patientPreconfigID: number;
    defaultPreconfig: Pick<Preconfiguration, 'id' | 'name'>;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    refreshPatientSilently: () => Promise<void>;
}

const normalizeWeekDays = (days?: number[] | null) => (days ?? []).join(',');

const areSheetParamsEqual = (
    current: PreconfigSheetParameter[],
    base: PreconfigSheetParameter[]
) => {
    const currentMap = new Map(
        current
            .filter(p => p.sheet_parameter_id)
            .map(p => [p.sheet_parameter_id as string, p])
    );

    const baseMap = new Map(
        base
            .filter(p => p.sheet_parameter_id)
            .map(p => [p.sheet_parameter_id as string, p])
    );

    if (currentMap.size !== baseMap.size) return false;

    for (const [key, currentParam] of currentMap.entries()) {
        const baseParam = baseMap.get(key);
        if (!baseParam) return false;

        if ((currentParam.min_limit ?? null) !== (baseParam.min_limit ?? null)) return false;
        if ((currentParam.max_limit ?? null) !== (baseParam.max_limit ?? null)) return false;
        if ((currentParam.frecuency ?? null) !== (baseParam.frecuency ?? null)) return false;
        if ((currentParam.monthly_frequency_day ?? null) !== (baseParam.monthly_frequency_day ?? null)) return false;
        if (normalizeWeekDays(currentParam.semanal_frequency) !== normalizeWeekDays(baseParam.semanal_frequency)) return false;
        if ((currentParam.frequency_duration ?? null) !== (baseParam.frequency_duration ?? null)) return false;
        if ((currentParam.reminder_time ?? null) !== (baseParam.reminder_time ?? null)) return false;
    }

    return true;
};

const areInquiriesEqual = (
    current: Array<Omit<PatientInquiry, 'patient_id' | 'start_date'>>,
    base: PreconfigInquiry[]
) => {
    const currentMap = new Map(current.map(i => [i.inquiry_id, i]));
    const baseMap = new Map(base.map(i => [i.inquiry_id, i]));

    if (currentMap.size !== baseMap.size) return false;

    for (const [key, currentInquiry] of currentMap.entries()) {
        const baseInquiry = baseMap.get(key);
        if (!baseInquiry) return false;

        if ((currentInquiry.frecuency ?? null) !== (baseInquiry.frecuency ?? null)) return false;
        if ((currentInquiry.monthly_frequency_day ?? null) !== (baseInquiry.monthly_frequency_day ?? null)) return false;
        if (normalizeWeekDays(currentInquiry.semanal_frequency) !== normalizeWeekDays(baseInquiry.semanal_frequency)) return false;
        if ((currentInquiry.frequency_duration ?? null) !== (baseInquiry.frequency_duration ?? null)) return false;
        if ((currentInquiry.reminder_time ?? null) !== (baseInquiry.reminder_time ?? null)) return false;
    }

    return true;
};

const PatientPreconfigDialog: React.FC<Props> = ({
    patient,
    patientPreconfigID,
    defaultPreconfig,
    setShow,
    refreshPatientSilently
}) => {
    const defaultPreconfigSheetParams = useRef<PreconfigSheetParameter[]>([]);
    const defaultPreconfigInquiries = useRef<Preconfiguration['preconfig_inquiries']>([]);
    const initializedRef = useRef<number | null>(null);

    const [isSaving, setIsSaving] = useState(false);

    const { sheetParameters } = useFetchPreconfigData();

    const {
        loadingConstants,
        loadingInquiries,
        errorConstants,
        errorInquiries,
        basePreconfigSheetParameters,
        basePreconfigInquiries,
        preconfigSheetParameters,
        preconfigInquiries,
        patientPreconfigDetail,
        refetchPatientPreconfig,
    } = useFetchPatientPreconfig(patientPreconfigID, patient.id);

    const { saveOverride, deleteOverride } = useSavePatientPreconfig(patientPreconfigID);

    const {
        preconfigPatientInquiries,
        handleChangeInquiry,
        handleChangeInquiryFreq,
        handleChangeInquiryReminder,
        restartPatientInquiries,
    } = usePatientPreconfigInquiries(patient.id);

    const {
        patientSheetParameters,
        restartSheetParameters,
        dispatchPreconfigSheetParams,
    } = usePatientPreconfigParameters();

    useEffect(() => {
        initializedRef.current = null;
    }, [patientPreconfigID]);

    useEffect(() => {
        if (initializedRef.current === patientPreconfigID) return;
        if (!patientPreconfigDetail) return;

        defaultPreconfigSheetParams.current = basePreconfigSheetParameters ?? [];
        defaultPreconfigInquiries.current = basePreconfigInquiries ?? [];

        restartSheetParameters(preconfigSheetParameters ?? []);
        restartPatientInquiries(preconfigInquiries ?? []);

        initializedRef.current = patientPreconfigID;
    }, [
        patientPreconfigID,
        patientPreconfigDetail,
        basePreconfigSheetParameters,
        basePreconfigInquiries,
        preconfigSheetParameters,
        preconfigInquiries,
        restartSheetParameters,
        restartPatientInquiries
    ]);

    const isCurrentlyAtDefaults = useMemo(() => {
        return (
            areSheetParamsEqual(patientSheetParameters, defaultPreconfigSheetParams.current) &&
            areInquiriesEqual(preconfigPatientInquiries, defaultPreconfigInquiries.current ?? [])
        );
    }, [patientSheetParameters, preconfigPatientInquiries]);

    const handleRestoreLocal = useCallback(() => {
        if (isSaving) return;

        restartSheetParameters(defaultPreconfigSheetParams.current ?? []);
        restartPatientInquiries(defaultPreconfigInquiries.current ?? []);
    }, [isSaving, restartPatientInquiries, restartSheetParameters]);

    const handleConfirm = () => {
        if (!patientPreconfigDetail || isSaving) return;

        setIsSaving(true);

        if (isCurrentlyAtDefaults) {
            deleteOverride(
                async () => {
                    try {
                        await refetchPatientPreconfig();
                        await refreshPatientSilently();
                        setShow(false);
                    } finally {
                        setIsSaving(false);
                    }
                },
                () => {
                    setIsSaving(false);
                }
            );
            return;
        }

        saveOverride(
            patientSheetParameters,
            preconfigPatientInquiries,
            async () => {
                try {
                    await refreshPatientSilently();
                    setShow(false);
                } finally {
                    setIsSaving(false);
                }
            },
            () => {
                setIsSaving(false);
            }
        );
    };

    return (
        <div className={`dialog ${styles.dialog}`}>
            <PatientPreconfigHeader
                patient={patient}
                preconfig={defaultPreconfig}
                setShow={setShow}
            />

            <div className={styles.content}>
                <ErrorSpinnerWrapper
                    loading={loadingConstants || isSaving}
                    error={errorConstants}
                >
                    <PatientPreconfigParameters
                        sheetParameters={sheetParameters ?? []}
                        patientSheetParameters={patientSheetParameters}
                        preconfigSheetParameters={defaultPreconfigSheetParams.current}
                        dispatchPreconfigSheetParams={dispatchPreconfigSheetParams}
                    />
                </ErrorSpinnerWrapper>

                <ErrorSpinnerWrapper
                    loading={loadingInquiries || isSaving}
                    error={errorInquiries}
                >
                    <PreconfigPollTable
                        compareInquiries={defaultPreconfigInquiries.current}
                        preconfigInquiries={preconfigPatientInquiries}
                        handleChangeInquiry={handleChangeInquiry}
                        handleChangeInquiryFreq={handleChangeInquiryFreq}
                        handleChangeInquiryReminder={handleChangeInquiryReminder}
                    />
                </ErrorSpinnerWrapper>
            </div>

            <div className={styles.bottomContainer}>
                <PatientPreconfigRestore
                    defaultPreconfigID={defaultPreconfig.id}
                    patientID={patient.id}
                    handleUnassign={handleRestoreLocal}
                />
                <CancelConfirmButtons
                    onCancel={() => {
                        if (isSaving) return;
                        setShow(false);
                    }}
                    onConfirm={handleConfirm}
                    customStyles={{ marginTop: 0 }}
                />
            </div>
        </div>
    );
};

export default PatientPreconfigDialog;